import * as React from 'react'
import {Box, Table, TableHead, TableBody, TableRow, TableCell, Typography} from '@mui/material'
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip as RechartsTooltip, Legend,
} from 'recharts'
import {dataGridWrapSx, headerCellSx, cellSx, sectionCardSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, WHITE, CHART_PALETTE} from '../../../styles/foundation/colors'
import {weeklyBreakdown, courierBreakdown} from './state/analytics'

const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const chartTitleSx = {fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.md, color: NAVY, mb: 1}
const axisTickStyle = {fontFamily: FONT, fontSize: 11}
const tooltipBoxSx = {
  bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '8px',
  p: 1, boxShadow: '0 2px 8px rgba(0,0,0,0.12)', maxWidth: 220,
}
const tooltipLabelSx = {fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: 12, color: NAVY, mb: 0.25}
const tooltipRowSx = {fontFamily: FONT, fontSize: 12}

/**
 * Recharts hover tooltip for this tab's two count-only charts. Every
 * payload value is a plain shipment/discrepancy count now (never money —
 * see the module doc comment below), so it renders the raw number with no
 * currency formatting and no EXCESS_NOTE disclaimer to carry.
 */
export function ChartTooltip({active, payload, label}) {
  if (!active || !payload || !payload.length) return null
  return (
    <Box sx={tooltipBoxSx}>
      {label != null && <Typography sx={tooltipLabelSx}>{label}</Typography>}
      {payload.map((p) => (
        <Typography key={p.dataKey || p.name} sx={{...tooltipRowSx, color: p.color || NAVY}}>
          {p.name}: {p.value}
        </Typography>
      ))}
    </Box>
  )
}

function ChartCard({title, children}) {
  return (
    <Box sx={{...sectionCardSx, mb: 2}}>
      <Typography sx={chartTitleSx}>{title}</Typography>
      <Box sx={{width: '100%', height: 260}}>{children}</Box>
    </Box>
  )
}

/**
 * Recovery Analytics — a week-over-week and courier-over-courier read on
 * the same range-filtered rows the KPI strip and SKU Weight Intelligence
 * tab use (see index.js's `rows`), so switching the date range moves this
 * tab's numbers in step with the KPI cards above it.
 *
 * Every measure here is a shipment count, never money — "Raised"/"Cases"
 * is weeklyBreakdown's `cases` (every row in the bucket), "Recovered" is
 * its `recoveredCases`, and the courier chart/table plot `discrepancies`.
 * "Recovered" is defined identically to state/query.js's queryTotals (see
 * state/analytics.js's isRecovered, and analytics.test.js's reconciliation
 * test) — rows accepted by the courier or by Prozo.
 */
export default function RecoveryAnalytics({rows, weeks: weeksProp, couriers: couriersProp}) {
  // Server-computed on a live mount (over every row under the filters, not the page in hand);
  // derived from the dataset on a seeded one.
  const derivedWeeks = React.useMemo(() => weeklyBreakdown(rows), [rows])
  const derivedCouriers = React.useMemo(() => courierBreakdown(rows), [rows])
  const weeks = weeksProp || derivedWeeks
  const couriers = couriersProp || derivedCouriers
  const isEmpty = (!weeks || weeks.length === 0) && (!couriers || couriers.length === 0)

  if (isEmpty) {
    return (
      <Box sx={{...dataGridWrapSx, p: 3, textAlign: 'center'}}>
        <Typography sx={subtleTextSx}>No discrepancies in this date range to analyze.</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <ChartCard title='Raised vs. recovered by week'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={weeks} margin={{top: 8, right: 16, left: 8, bottom: 24}}>
            <CartesianGrid strokeDasharray='3 3' stroke={BORDER} />
            <XAxis dataKey='label' tick={axisTickStyle} interval={0} angle={-20} textAnchor='end' height={50} />
            <YAxis tick={axisTickStyle} width={64} allowDecimals={false} />
            <RechartsTooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={axisTickStyle} />
            <Bar dataKey='cases' name='Raised' fill={CHART_PALETTE[0]} radius={[4, 4, 0, 0]} />
            <Bar dataKey='recoveredCases' name='Recovered' fill={CHART_PALETTE[1]} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <Box sx={{...dataGridWrapSx, mb: 3}}>
        <Table sx={{minWidth: 640}}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Week</TableCell>
              <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Raised</TableCell>
              <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Recovered</TableCell>
              <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Share</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {weeks.map((w) => (
              <TableRow key={w.weekIndex} hover>
                <TableCell sx={cellSx}>{w.label}</TableCell>
                <TableCell sx={{...cellSx, textAlign: 'right'}}>{w.cases}</TableCell>
                <TableCell sx={{...cellSx, textAlign: 'right'}}>{w.recoveredCases}</TableCell>
                <TableCell sx={{...cellSx, textAlign: 'right'}}>{w.share}%</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <ChartCard title='Discrepancies by courier'>
        <ResponsiveContainer width='100%' height='100%'>
          <BarChart data={couriers} layout='vertical' margin={{top: 8, right: 24, left: 8, bottom: 8}}>
            <CartesianGrid strokeDasharray='3 3' stroke={BORDER} />
            <XAxis type='number' tick={axisTickStyle} allowDecimals={false} />
            <YAxis type='category' dataKey='courier' tick={axisTickStyle} width={90} />
            <RechartsTooltip content={<ChartTooltip />} />
            <Bar dataKey='discrepancies' name='Discrepancies' radius={[0, 4, 4, 0]}>
              {couriers.map((c, i) => (
                <Cell key={c.courier} fill={CHART_PALETTE[i % CHART_PALETTE.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <Box sx={dataGridWrapSx}>
        <Table sx={{minWidth: 480}}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Courier</TableCell>
              <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Discrepancies</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {couriers.map((c) => (
              <TableRow key={c.courier} hover>
                <TableCell sx={cellSx}>{c.courier}</TableCell>
                <TableCell sx={{...cellSx, textAlign: 'right'}}>{c.discrepancies}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  )
}
